export const VETERANS_BENEFITS_TEMPLATE = `# Honoring {{deceased_first_name}}'s Service Through VA Benefits

This guide helps you request VA survivor benefits, burial support, and memorial items after the Veteran's passing. You do not need to be the executor for most VA requests. While this process can feel overwhelming, the most important things to keep in mind are your relationship to the Veteran and any costs you've handled.

| | |
| --- | --- |
| VA phone | 1-800-827-1000 |
| Best time to call | Tuesday through Thursday between 8:00 AM and 10:00 AM ET to minimize hold times. |
| Find accredited help | [Get Help From A VA Accredited Representative Or VSO](https://www.va.gov/vso/) |

# Important to Know

- Burial allowance may apply to the person who paid or is responsible for burial costs.
- Memorial items can often be requested more broadly, but VA or the issuing office will confirm whether the request can be completed.
- Since VA rates can change every year, it's always best to check the current numbers on their official website.

# Master Document List

**What this does:** Helps you gather the necessary records to prove military service and your relationship to the deceased.

- Official Death Certificate
- Marriage Certificate (generally required for spouses to prove relationship)
- Birth Certificates (generally required for children or parents proving relationship)
- DD-214 (Proof of Service)

# Universal Submission Rules

- You must write the Veteran's full name and Social Security Number (or VA File Number) on the top of every single page you submit.
- Never send original documents like an original death certificate.
- Submit clear photocopies of the documents instead.
- Do not staple or bind the papers.

# How to Submit Forms (Centralized Intake Centers)

Sending a form to the wrong center will cause significant delays.

| Center | Address | Use for |
| --- | --- | --- |
| Janesville Pension Intake Center | P.O. Box 5365, Janesville, WI 53547-5365 | Benefits/pension claims like Intent to File, DIC/Survivors Pension, Burial allowance |
| NCA Evidence Intake Center | PO Box 5237, Janesville, WI 53547 | Memorial items like the Presidential Memorial Certificate |

# If You Do Not Have Proof of Service

Most VA benefits and memorial requests require proof of military service. The DD-214 is the most common document used for this. The National Archives recommends using the DD-214 for most requests.

If you do not have it, [request military records here](https://www.archives.gov/veterans/military-service-records).

**SF-180 Cheat Sheet: Request Pertaining to Military Records**

You can find the form here: [Request Military Personnel Records Using Standard Form 180](https://www.archives.gov/veterans/military-service-records/standard-form-180.html)

| Form row | What to enter |
| --- | --- |
| Name used during service | {{deceased_full_name}} |
| Social Security Number | {{deceased_ss_number}} |
| Service Number | Highly recommended for veterans who served before 1974; check discharge papers or old ID cards. |
| Date of Birth | {{date_of_birth}} |
| Place of Birth | Enter if known |
| Branch of Service | {{branch_of_service}} |
| Dates of Service | {{service_start_date}} to {{service_end_date}} |
| Purpose | Benefits |
| Records requested | DD Form 214 or separation documents |
| Requester name | {{first_name}} {{last_name}} |
| Relationship to veteran | {{user_relationship}} |
| Signature and date | User signs and dates |
| Proof of death | Attach death certificate, funeral home letter, or obituary |

{{#if is_spouse}}
# DIC / Survivors Pension / Benefits Earned but Not Yet Paid

As {{deceased_first_name}}'s surviving spouse, you may be able to apply for Dependency and Indemnity Compensation, Survivors Pension, and/or benefits earned but not yet paid.

## What to Do

If you are not ready to file the full application, file VA Form 21-0966 first.

This is the Intent to File form. This form is intended to preserve a potential effective date for benefits. If approved, the VA may pay from the date you filed this "Intent." VA says it can help make sure you get paid from the earliest possible date while you take your time gathering the rest of the paperwork if you are still gathering information.

You can find the form here: [VA Form 21-0966](https://www.va.gov/find-forms/about-form-21-0966/)

Mail completed forms to: VA Pension Intake Center, P.O. Box 5365, Janesville, WI 53547-5365

**VA Form 21-0966 Cheat Sheet:**

| Form row | What to enter |
| --- | --- |
| 1. Veteran's Name | {{deceased_full_name}} |
| 2. Social Security Number | {{deceased_ss_number}} |
| 3. Have You Ever Filed a VA Claim? | Answer based on what you know |
| 4. VA File Number, if applicable | Enter if known |
| 5. Date of Birth | {{date_of_birth}} |
| 6. Veteran's Service Number, if applicable | Enter if known |
| 7. Mailing Address, if applicable | Enter "DECEASED" |
| 8. Telephone Number | Enter "DECEASED" |
| 9. E-mail Address, if applicable | Enter "DECEASED" |
| 10. Claimant's Name | {{first_name}} {{last_name}} |
| 11. Social Security Number | User SSN |
| 12. Have You Ever Filed a VA Claim? | Answer Yes or No |
| 13. VA File Number, if applicable | Enter if known |
| 14. Relationship to Veteran | Check Spouse |
| 15. Claimant's Date of Birth | User DOB |
| 16. Mailing Address, if applicable | Your mailing address |
| 17. Telephone Number | Your phone number |
| 18. E-mail Address, if applicable | User email |
| 19. I intend to file for the general benefit(s) checked below | Check "Survivors Pension and/or Dependency and Indemnity Compensation (DIC)" |
| 20. Signature of Veteran/Claimant/Authorized Agent | User signs |
| 21. Date Signed | Date signed |
| 22. Name of Attorney, Agent, or Veterans Service Organization | Complete only if applicable |

Then file VA Form 21P-534EZ.

You can find the form here: [VA Form 21P-534EZ (PDF)](https://www.va.gov/find-forms/about-form-21p-534ez/)

**Where to submit:**
- Mail to: VA Pension Intake Center, P.O. Box 5365, Janesville, WI 53547-5365.
- Fax: If you have a scanner, use the VA's "QuickSubmit" portal instead of faxing to ensure you receive an immediate digital receipt of your submission.

**VA Form 21P-534EZ Cheat Sheet:**

| Form section / row name | What to enter |
| --- | --- |
| Section I: {{deceased_first_name}}'s Information | {{deceased_full_name}}, SSN {{deceased_ss_number}}, date of birth {{date_of_birth}}, date of death {{date_of_death}} |
| Veteran's Mailing Address, if applicable | Enter "DECEASED" |
| Veteran's Telephone Number, if applicable | Enter "DECEASED" |
| Section II: Your Information | {{first_name}} {{last_name}}, user SSN, date of birth, mailing address, phone, email |
| Section III: Veteran's Service Information | Branch {{branch_of_service}}, service dates {{service_start_date}} to {{service_end_date}}, discharge information if known |
| Section IV: Marital Information | Marriage date {{marriage_date}}, place of marriage {{marriage_place}}, and current marital details |
| Section V: Marital History | List prior marriages for you and the veteran, if any |
| Section VI: Child of Veteran Information | Complete only if applicable or if VA tells you to |
| Section VII: D.I.C. | Complete if claiming DIC. Include details related to service-connected death if applicable |
| Section VIII: Nursing Home or Increased Survivors Entitlement | Complete only if claiming aid and attendance, housebound, or nursing home-related increased benefits |
| Section IX: Financial details | Complete if applying for Survivors Pension or if VA asks for income/net worth details |
| Section X: Information About Your Medical or Other Expenses | Enter unreimbursed medical or other deductible expenses, if relevant |
| Section XI: Direct Deposit Information | Bank name, routing number, account number, account type |
| Section XII: Claim Certification and Signature | Read certification, sign, and date |
| Section XIII: Witnesses to Signature | Complete only if needed |
| Section XIV: Alternate Signer Certification and Signature | Complete only if someone signs for the claimant |

Check current VA rates here:
- [Current DIC Rates For Spouses And Dependents](https://www.va.gov/disability/survivor-dic-rates/)
- [Current Survivors Pension Benefit Rates](https://www.va.gov/pension/survivors-pension-rates/)
{{/if}}

{{#if is_parent}}
# Dependent Parent VA Benefits

As a parent of {{deceased_first_name}}, eligibility is more specific. VA has a separate parent form for Dependency and Indemnity Compensation by parent(s).

You can find the form here: [VA Form 21P-535](https://www.va.gov/find-forms/about-form-21p-535/)

**Important:** You must download the latest version directly from the VA website to ensure the income limit tables are current for the present year.

VA Form 21P-535 is titled Application for Dependency and Indemnity Compensation by Parent(s), including benefits earned but not yet paid and Death Compensation when applicable.

## What to Do

Call VA at 1-800-827-1000.

**Say:**

> "Hi, I'm calling because {{deceased_full_name}} was a Veteran and has passed away. I would like to ask whether I may qualify for parent DIC benefits."

If VA confirms this applies, use VA Form 21P-535.

**VA Form 21P-535 Cheat Sheet:**

| Form section | What to prepare |
| --- | --- |
| Veteran's Identification Information | {{deceased_full_name}}, SSN {{deceased_ss_number}}, date of birth {{date_of_birth}}, date of death {{date_of_death}} |
| Claimant Information | {{first_name}} {{last_name}}, SSN, date of birth, mailing address, phone, email |
| Veteran's Active Duty Service | Branch {{branch_of_service}}, service dates {{service_start_date}} to {{service_end_date}}, service number if known |
| Veteran's Parent(s) Information | Parent details, relationship to veteran, and whether the veteran was under parental control before age of majority |
| Parent's Marital History | Parent's marital status and spouse details, if applicable |
| Nursing Home / Aid and Attendance | Complete only if claiming aid and attendance or nursing home-related benefits |
| Parent's Income | Social Security, wages, retirement, interest, insurance, and other income |
| Medical / Burial / Other Expenses | Medical, last illness, burial, or other unreimbursed expenses |
| Direct Deposit Information | Bank name, routing number, account number |
| Certification and Signature | Parent signs and dates |
| Remarks | Use only if extra explanation is needed |
{{/if}}

{{#if is_other}}
# VA Survivor Benefits May Not Apply Directly to You

Call VA if you are unsure: 1-800-827-1000
{{/if}}

# Burial Allowance

You may be able to apply for VA burial benefits if you paid or are responsible for funeral, burial, or transportation costs and will not be fully reimbursed from another source. VA decides whether reimbursement is available and how much can be paid.

You can find the form here: [VA Form 21P-530EZ (PDF)](https://www.va.gov/find-forms/about-form-21p-530ez/)

## What to Do

- Use VA Form 21P-530EZ.
- Attach itemized receipts and proof of payment, if available.
- Submit through the VA method shown on the form or VA page.
- Mail completed forms to: VA Pension Intake Center, P.O. Box 5365, Janesville, WI 53547-5365

**Note:** Current VA burial allowance information: [Veterans Burial Allowance And Transportation Benefits](https://www.va.gov/burials-memorials/veterans-burial-allowance/)

**VA Form 21P-530EZ Cheat Sheet: Burial Benefits**

| Form section | What to enter |
| --- | --- |
| Section I: Personal Identification of Veteran | {{deceased_full_name}}, SSN {{deceased_ss_number}}, date of birth {{date_of_birth}}, date of death {{date_of_death}} |
| Veteran's Mailing Address, if applicable | Enter "DECEASED" |
| Veteran's Telephone Number, if applicable | Enter "DECEASED" |
| Section II: Claimant's Information | {{first_name}} {{last_name}}, mailing address, phone, email, relationship to veteran |
| Section III: Veteran's Service Information | Branch {{branch_of_service}}, service dates {{service_start_date}} to {{service_end_date}}, service number if known |
| Section IV: Where {{deceased_first_name}} is resting | Cemetery or final resting place name and address, burial or interment details |
| Section V: Claim for Burial Allowance | Funeral/burial costs, amount paid, who paid, whether another source reimbursed costs |
| Section VI: Claim for Plot and/or Transportation Allowance | Complete if claiming plot, interment, or transportation reimbursement |
| Section VII: Claim Certification and Signatures | Read certification, sign, and date |
| Section VIII: Witnesses to Signature | Complete only if needed |
| Section IX: Alternate Signer Certification and Signature | Complete only if someone signs for the claimant |

# Headstone or Marker

VA Form 40-1330 is used to request a standard government headstone or marker.

Official link: [va.gov/forms/40-1330/](https://www.va.gov/find-forms/about-form-40-1330/)

## What to Do

- Use VA Form 40-1330.
- Confirm cemetery details before filing.
- If the veteran is buried in a VA national cemetery, the cemetery usually handles the government marker process.
- **Where to submit:** Submit via the [QuickSubmit tool](https://access.va.gov) for faster tracking, or mail to the Janesville address provided.

**VA Form 40-1330 Cheat Sheet: Headstone or Marker**

| Form section / row name | What to enter |
| --- | --- |
| Deceased Veteran / Service Member Information | {{deceased_full_name}}, SSN if requested, date of birth {{date_of_birth}}, date of death {{date_of_death}} |
| Service Information | Branch {{branch_of_service}}, service dates {{service_start_date}} to {{service_end_date}}, discharge information if known |
| Type of Headstone or Marker Requested | Select the requested marker type from the form options |
| Inscription Information | Veteran's legal name, branch, dates, optional emblem or authorized inscription |
| Cemetery / Final Resting Place Information | Cemetery name, full address, grave/plot/niche if known |
| Applicant Information | {{first_name}} {{last_name}}, mailing address, phone, email, relationship |
| Certification and Signature | User signs and dates |
| Cemetery / Consignee Information | Complete with cemetery or delivery details, if required |

# Presidential Memorial Certificate

A Presidential Memorial Certificate is an engraved paper certificate signed by the current president to honor a deceased veteran's service. VA Form 40-0247 is the official request form.

You can find the form here: [VA Form 40-0247](https://www.va.gov/find-forms/about-form-40-0247/)

## What to Do

- Use VA Form 40-0247, or request online through VA if available.
- Request the number of copies needed for family members.
- Make sure the veteran's name is written exactly as it should appear.
- **Where to submit:** Mail VA Form 40-0247 to: NCA FP Evidence Intake Center, PO Box 5237, Janesville, WI 53547. Fax: 1-800-455-7143

**VA Form 40-0247 Cheat Sheet:**

| Form section / row name | What to enter |
| --- | --- |
| Type of Request | New request, replacement, or correction, as applicable |
| Veteran / Servicemember Information | {{deceased_full_name}}, branch {{branch_of_service}}, service dates if requested |
| Veteran's Date of Death | {{date_of_death}} |
| Veteran's Social Security Number or Service Number | {{deceased_ss_number}} or service number if known |
| Name and Mailing Address of Person Requesting Certificate | {{first_name}} {{last_name}}, your mailing address |
| Number of Certificates Requested | Number needed |
| Relationship to Veteran | {{user_relationship}} |
| Signature and Date | User signs and dates, if required by the version used |

# In-Person Tasks: Burial Flag

**DO NOT MAIL THIS FORM**

A burial flag is a U.S. flag used to honor the veteran's service. VA Form 27-2008 is the official form for this request.

Please keep in mind that the burial flag request must be handled in person rather than by mail. You can take the form to a local funeral home or post office.

Official link: [VA Form 27-2008 (PDF)](https://www.va.gov/find-forms/about-form-27-2008/)

## What to Do

- Complete VA Form 27-2008.
- Locate a funeral home, VA office, or USPS location that stocks burial flags.
- Deliver the form in person; do not mail this specific request.
- Bring proof of service if available, such as DD-214.

**VA Form 27-2008 Cheat Sheet: Burial Flag**

| Form row | What to enter |
| --- | --- |
| Name of Deceased Veteran | {{deceased_full_name}} |
| Branch of Service | {{branch_of_service}} |
| Service Number | Enter if known |
| Social Security Number | {{deceased_ss_number}} |
| Date Entered Active Duty | {{service_start_date}} |
| Date Released from Active Duty | {{service_end_date}} |
| Date of Birth | {{date_of_birth}} |
| Date of Death | {{date_of_death}} |
| Name of Person Entitled to Receive Flag | {{first_name}} {{last_name}} |
| Relationship to Deceased Veteran | {{user_relationship}} |
| Address of Person Entitled to Receive Flag | Your mailing address |
| Signature of Person Requesting Flag | User signs |
| Date | Date signed |
| Funeral Director / Postmaster / VA Official Certification | Completed by the office issuing the flag |

# Escalation for Complex Cases

- If there are multiple claimants or a dispute regarding who paid for burial costs, contact a VA-accredited representative or legal professional.
- If the VA requests additional evidence you do not have, contact a Veterans Service Organization (VSO) for assistance.

# Quick Checklist

- [ ] Decide which benefits you'd like to apply for first.
- [ ] Gather DD-214 or request service records if missing
- [ ] Gather death certificate
{{#if is_spouse}}
- [ ] For DIC / Survivors Pension, file Intent to File if not ready to submit the full claim
{{/if}}
- [ ] Send in the form that matches the help you need.
- [ ] Save copies of everything submitted
- [ ] Track the claim or request
- [ ] Contact VA or a VSO if VA asks for more evidence`;
